import { useState, useEffect } from "react";
import React, { useRef } from "react";

import { useNavigate } from "react-router-dom";
import {
  Box, Container, Radio, RadioGroup, Typography, Grid,
  TextField, Button, Snackbar, Alert, Autocomplete, Paper,
  useMediaQuery, useTheme, Checkbox, FormHelperText
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import CircularProgress from "@mui/material/CircularProgress";
import { useMutation } from "@apollo/client";
import { ADD_MEMBER_MUTATION } from "../graphql/mutations"; // adjust path as needed
import { isValid } from "date-fns";

const Register = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [addMember, { loading: isLoading }] = useMutation(ADD_MEMBER_MUTATION);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    state: "",
    city: "",
    district: "",
    dob: "",
    pincode: "",
    mobile: "",
    otp: "",
    identityProofType: "",
    identityProofNumber: "",
    panCard: " ",
    panCardNumber: "",
  });

  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showOtpField, setShowOtpField] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [timer, setTimer] = useState(0);

  useEffect(() => {
    // Timer logic for OTP resend button
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleClear = () => {
    setFormData({
      firstName: "",
      lastName: "",
      state: "",
      city: "",
      district: "",
      dob: "",
      pincode: "",
      mobile: "",
      otp: "",
      identityProofType: "",
      identityProofNumber: "",
      panCard: "",
      panCardNumber: "",
    });
    setShowOtpField(false);
    setOtpSent(false);
    setOtpVerified(false);
    setTimer(0);
  };

  const validateForm = () => {
    let newErrors = {};

    // First Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First Name is required";
    }

    // Last Name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last Name is required";
    }

    // State validation
    if (!formData.state.trim()) {
      newErrors.state = "State is required";
    }

    // City validation
    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }

    // District validation
    if (!formData.district.trim()) {
      newErrors.district = "District is required";
    }

    // Date of Birth validation
    if (!formData.dob.trim()) {
      newErrors.dob = "Date of Birth is required";
    } else {
      const selectedDate = new Date(formData.dob);
      const today = new Date();
      const minDate = new Date("1900-01-01");
      const minAllowedDate = new Date();
      minAllowedDate.setFullYear(today.getFullYear() - 18);
      today.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);
      minDate.setHours(0, 0, 0, 0);
      minAllowedDate.setHours(0, 0, 0, 0);

      if (selectedDate >= today) {
        newErrors.dob = "Date of Birth must be in the past";
      } else if (selectedDate < minDate) {
        newErrors.dob = "Date of Birth must be after 01-01-1900";
      } else if (selectedDate > minAllowedDate) {
        newErrors.dob = "Minimum age required to register is 18 years.";
      }
    }

    // Pincode validation
    if (!formData.pincode.trim()) {
      newErrors.pincode = "Pincode is required";
    } else if (formData.pincode.length !== 6) {
      newErrors.pincode = "Pincode must be exactly 6 digits";
    }
    // Mobile Number validation
    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile Number is required";
    } else if (formData.mobile.length !== 10) {
      newErrors.mobile = "Mobile number must be exactly 10 digits";
    }

    // OTP validation only if OTP field is shown
    if (showOtpField && !formData.otp.trim()) {
      newErrors.otp = "OTP is required";
    } else if (showOtpField && formData.otp.length !== 6) {
      newErrors.otp = "OTP must be 6 digits";
    }

    // Add validation only if ID proof type is selected but number is empty
    if (formData.identityProofType && !formData.identityProofNumber) {
      newErrors.identityProofNumber = "ID Proof Number is required";
    }

    // Add validation only if PAN is selected as "Yes" but number is empty
    if (formData.panCard === "Yes" && !formData.panCardNumber) {
      newErrors.panCardNumber = "PAN Number is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRadioChange = (e) => {
    const selectedType = e.target.value;
    setFormData((prev) => ({
      ...prev,
      identityProofType: selectedType,
    }));
  };

  const handlePanChange = (e) => {
    const value = e.target.value;
    setFormData({
      ...formData,
      panCard: value,
      panCardNumber: value === "No" ? "" : formData.panCardNumber
    });
  };

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const navigate = useNavigate();

  const districts = ["Ariyalur", "Chengalpattu", "Chennai", "Coimbatore", "Cuddalore", "Dharmapuri",
    "Dindigul", "Erode", "Kallakurichi", "Kancheepuram", "Kanyakumari", "Karur", "Karaikal", "Krishnagiri",
    "Madurai", "Mayiladuthurai", "Nagapattinam", "Namakkal", "Nilgiris", "Perambalur", "Pudukkottai",
    "Ramanathapuram", "Ranipet", "Salem", "Sivaganga", "Tenkasi", "Thanjavur", "Theni", "Thoothukudi",
    "Thiruchirappalli", "Tirunelveli", "Tirupathur", "Tiruppur", "Tiruvallur", "Tiruvannamalai", "Tiruvarur",
    "Vellore", "Viluppuram", "Virudhunagar", "Other"
  ];

  const states = ["Tamil Nadu", "Other"];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });

    // Show OTP field when mobile number is entered completely
    if (e.target.name === "mobile" && e.target.value.length === 10) {
      setShowOtpField(true);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleSendOTP = async () => {

    if (formData.mobile.length !== 10) {
      setSnackbarOpen(true);
      setSnackbarMessage("Please enter a valid 10-digit mobile number"); setSnackbarSeverity("error");
      return;
    }
    const mobile = formData.mobile;
    try {
      const response = await fetch('https://gcp-poc-with-apollo-1081525845464.asia-south1.run.app/reg-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile, }),
      });
      const data = await response.json();
      if (data.status === 'error') {
        setSnackbarMessage(data.message || "OTP verification failed. Please try again.");
        
        setSnackbarSeverity("error");
      }
      setTimer(30); // Start 30 second timer
      setOtpSent(true);
      setSnackbarOpen(true);
      setSnackbarMessage("OTP sent to your mobile number");
      setSnackbarSeverity("success");
      setOtpSent(true);
    } catch (error) {
      setSnackbarMessage(error.message || "Failed to send OTP. Please try again.");

      setSnackbarSeverity("error");
    }
  };

  const handleVerifyOTP = async(e) => {
    e.preventDefault();

    if (formData.mobile.length !== 10) {
      setSnackbarMessage("Please enter a valid 10-digit mobile number.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (formData.otp.length !== 6) { // Assuming 6-digit OTP
      setSnackbarMessage("Please enter a valid 6-digit OTP");
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    const mobile =formData.mobile;
    const otp = formData.otp;


    try {
      const response = await fetch('https://gcp-poc-with-apollo-1081525845464.asia-south1.run.app/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile, otp }),
      });

      const data = await response.json();

      if (data.status === 'error') {
        setSnackbarOpen(true);
        setSnackbarMessage(data.message || "OTP verification failed. Please try again.");
        setSnackbarSeverity("error");
      }
      setSnackbarOpen(true);
      setSnackbarMessage("OTP verified successfully!");
      setSnackbarSeverity("success");
    }  catch (error) {
      setSnackbarOpen(true);
      setSnackbarMessage(error.message || "OTP verification failed. Please try again.");
      setSnackbarSeverity("error");
  }
  };
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 1. Run other form validations
    if (!validateForm()) {
      return;
    }

    // 2. Check if OTP is required and verified
    if (showOtpField && !otpVerified) {
      setSnackbarSeverity("error");
      setSnackbarMessage("Please verify your mobile number with OTP");
      setSnackbarOpen(true);
      return;
    }

    // 3. Validate privacy policy checkbox
    if (!formData.privacyPolicyAccepted) {
      setErrors((prev) => ({
        ...prev,
        privacyPolicyAccepted: "You must agree to the Privacy Policy.",
      }));
      return;
    } else {
      setErrors((prev) => ({
        ...prev,
        privacyPolicyAccepted: "",
      }));
    }

    // ✅ All checks passed – proceed with form submission logic
    try {
      // Your form submission logic here (e.g., API call)
      console.log("Form submitted", formData);
    } catch (error) {
      console.error("Submission error:", error);
      setSnackbarSeverity("error");
      setSnackbarMessage("Failed to submit form. Please try again.");
      setSnackbarOpen(true);
    }
    setIsSubmitted(true);

    const inputVariables = {
      First_Name: formData.firstName,
      Last_Name: formData.lastName,
      State: formData.state,
      City: formData.city,
      District: formData.district,
      DOB: formData.dob,
      Pincode: formData.pincode,
      Mobile_Number: formData.mobile,
      agreed: true,
      ...(formData.identityProofType && { ID_Proof_Type: formData.identityProofType }),
      ...(formData.identityProofNumber && { ID_Proof_Number: formData.identityProofNumber }),
      ...(formData.panCard && { Pan_Card: formData.panCard }),
      ...(formData.panCardNumber && { Pan_Number: formData.panCardNumber }),
    };

    try {
      const { data } = await addMember({ variables: inputVariables });
      setOpenSnackbar(true);
      setSnackbarMessage("Member added successfully."); // ✅ Success message
      handleClear();
      console.log("Member added:", data.addMember);
      navigate("/login");
      window.scrollTo(0, 0);
    } catch (error) {
      console.error("Error adding member:", error);
      console.log("Member added:", inputVariables);
      const errorMessage = error?.message || "Something went wrong while adding member.";
      setSnackbarOpen(true);
      setSnackbarMessage(errorMessage);
    }
  };
  const otpRefs = useRef([...Array(6)].map(() => React.createRef()));

  const handleOtpChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 1);
    if (!value) return;

    const newOtp = formData.otp.split("");
    newOtp[index] = value;
    const otpString = newOtp.join("");

    setFormData((prev) => ({ ...prev, otp: otpString }));

    // Focus next box
    if (index < 5 && value) {
      otpRefs.current[index + 1].current.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !e.target.value && index > 0) {
      otpRefs.current[index - 1].current.focus();
    }
  };


  return (
    <Box sx={{ backgroundColor: "#f8f9e9", py: 3, top: "150px", bottom: "150px" }}>
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          sx={{
            fontWeight: "500",
            color: "#2A289C",
            textAlign: "center",
            fontSize: isMobile ? "24px" : "32px"
          }}
        >
          New Member Registration
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "300",
            color: "#7A7841",
            my: 2,
            fontSize: isMobile ? "14px" : "16px",
            textAlign: "center",
            px: isMobile ? 2 : 0
          }}
        >
          Please provide your basic details to set up your account.
          {!isMobile && <br />} Once logged in, you'll be able to complete your profile and proceed with membership activation.
        </Typography>
        <form onSubmit={handleSubmit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSubmit(e);
            }
          }}>
          <Grid container justifyContent="center">
            <Grid item xs={12} sm={10} md={8}>
              <Box
                component="form"
                sx={{
                  backgroundColor: "#f8f9e9",
                  padding: isMobile ? 2 : 4,
                  borderRadius: "8px",
                  border: "1px solid #CEC35E",
                  boxShadow: 3,
                  width: "100%",
                  margin: "0 auto",
                }}
              >
                <Grid container spacing={isMobile ? 1 : 2}>
                  {/* First Name */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="First Name"
                      placeholder="First Name"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      inputProps={{ maxLength: 25, pattern: "[A-Za-z]*" }}
                      onInput={(e) => e.target.value = e.target.value.replace(/[^A-Za-z ]/g, '')}
                      sx={{ mb: 2 }}
                      error={!!errors.firstName}
                      helperText={errors.firstName || ""}
                      required
                    />
                  </Grid>

                  {/* Last Name */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Last Name"
                      placeholder="Last Name"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      inputProps={{ maxLength: 25, pattern: "[A-Za-z]*" }}
                      onInput={(e) => e.target.value = e.target.value.replace(/[^A-Za-z ]/g, '')}
                      sx={{ mb: 2 }}
                      error={!!errors.lastName}
                      helperText={errors.lastName || ""}
                      required
                    />
                  </Grid>

                  {/* State */}
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={states}
                      value={formData.state}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setFormData({ ...formData, state: newValue });
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="State"
                          variant="outlined"
                          size="small"
                          required
                          error={!!errors.state}
                          helperText={errors.state || ""}
                          sx={{
                            backgroundColor: "#f8f9e9",
                            mb: 2,
                            "& .MuiInputBase-input": {
                              caretColor: "transparent",
                              cursor: "pointer",
                            },
                          }}
                          InputLabelProps={{
                            shrink: true,
                            sx: {
                              backgroundColor: "#f8f9e9",
                              px: 1,
                              transform: "translate(14px, -6px) scale(0.75)",
                            },
                          }}
                          InputProps={{
                            ...params.InputProps,
                            readOnly: false,
                          }}
                        />
                      )}
                      disableClearable
                      fullWidth
                      autoHighlight
                      PaperComponent={(props) => (
                        <Paper
                          {...props}
                          sx={{
                            backgroundColor: "#f8f9e9",
                            maxHeight: isMobile ? '200px' : '300px',
                            overflowY: 'auto'
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* District */}
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={districts}
                      value={formData.district}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setFormData({ ...formData, district: newValue });
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="District"
                          variant="outlined"
                          size="small"
                          required
                          error={!!errors.district}
                          helperText={errors.district || ""}
                          sx={{
                            backgroundColor: "#f8f9e9",
                            mb: 2,
                            "& .MuiInputBase-input": {
                              caretColor: "transparent",
                              cursor: "pointer",
                            },
                          }}
                          InputLabelProps={{
                            shrink: true,
                            sx: {
                              backgroundColor: "#f8f9e9",
                              px: 1,
                              transform: "translate(14px, -6px) scale(0.75)",
                            },
                          }}
                          InputProps={{
                            ...params.InputProps,
                            readOnly: false,
                          }}
                        />
                      )}
                      disableClearable
                      fullWidth
                      autoHighlight
                      PaperComponent={(props) => (
                        <Paper
                          {...props}
                          sx={{
                            backgroundColor: "#f8f9e9",
                            maxHeight: isMobile ? '200px' : '300px',
                            overflowY: 'auto'
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* City */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="City"
                      placeholder="City"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      inputProps={{ maxLength: 30, pattern: "[A-Za-z]*" }}
                      onInput={(e) => e.target.value = e.target.value.replace(/[^A-Za-z ]/g, '')}
                      sx={{ mb: 2 }}
                      error={!!errors.city}
                      helperText={errors.city || ""}
                      required
                    />
                  </Grid>

                  {/* Pincode */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Pincode"
                      placeholder="Pincode"
                      type="text"
                      name="pincode"
                      value={formData.pincode}
                      onChange={(e) => {
                        const inputValue = e.target.value.replace(/\D/g, "");
                        if (inputValue.length <= 6) {
                          setFormData({ ...formData, pincode: inputValue });
                        }
                      }}
                      error={!!errors.pincode}
                      helperText={errors.pincode || ""}
                      sx={{ mb: 2 }}
                      required
                    />
                  </Grid>

                  {/* Date of Birth */}
                  <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Date of Birth"
                        format="YYYY-MM-DD"
                        value={formData.dob ? dayjs(formData.dob, "YYYY-MM-DD") : null}
                        onChange={(date) => setFormData({ ...formData, dob: date ? date.format("YYYY-MM-DD") : "" })}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            error: !!errors.dob,
                            helperText: errors.dob || "",
                            required: true,
                            sx: { mb: 2 }
                          },
                          popper: {
                            sx: {
                              '& .MuiPaper-root': {
                                backgroundColor: '#f8f9e9',
                              },
                            },
                          },
                        }}
                        disableFuture
                        maxDate={dayjs().subtract(18, "year")}
                      />
                    </LocalizationProvider>
                  </Grid>

                  {/* Mobile Number */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', gap: 1, width: '100%', mb: 2 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Mobile Number"
                        placeholder="Mobile Number"
                        type="text"
                        name="mobile"
                        value={formData.mobile}
                        onChange={(e) => {
                          const inputValue = e.target.value.replace(/\D/g, "");
                          if (inputValue.length <= 10) {
                            setFormData({ ...formData, mobile: inputValue });
                            if (inputValue.length === 10) {
                              setShowOtpField(true);
                            } else {
                              setShowOtpField(false);
                              setOtpSent(false);
                              setOtpVerified(false);
                            }
                          }
                        }}
                        error={!!errors.mobile}
                        helperText={errors.mobile || " "}
                        required
                      />
                      {formData.mobile.length === 10 && !otpVerified && (
                        <Button
                          variant="contained"
                          onClick={handleSendOTP}
                          disabled={timer > 0}
                          sx={{
                            backgroundColor: timer > 0 ? "#cccccc" : "#2A289C",
                            height: "40px",
                            minWidth: isMobile ? "80px" : "120px",
                            fontSize: isMobile ? "12px" : "14px",
                            boxShadow: "0px 2px 4px rgba(0,0,0,0.2)",
                            "&:hover": {
                              backgroundColor: timer > 0 ? "#cccccc" : "#1a1769"
                            }
                          }}
                        >
                          {otpSent ? (timer > 0 ? `${timer}s` : "Resend") : "Send OTP"}
                        </Button>
                      )}
                    </Box>
                  </Grid>

                  {showOtpField && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
                        {[...Array(6)].map((_, i) => (
                          <TextField
                            key={i}
                            inputRef={otpRefs.current[i]}
                            value={formData.otp[i] || ""}
                            onChange={(e) => handleOtpChange(e, i)}
                            onKeyDown={(e) => handleKeyDown(e, i)}
                            variant="outlined"
                            size="small"
                            inputProps={{
                              maxLength: 1,
                              style: {
                                textAlign: "center",
                                fontSize: "20px",
                                width: "40px",
                                height: "40px",
                                padding: 0
                              },
                            }}
                            disabled={!otpSent || otpVerified}
                            error={!!errors.otp}
                          />
                        ))}
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        {otpSent && !otpVerified && (
                          <Button
                            variant="contained"
                            onClick={handleVerifyOTP}
                            sx={{
                              backgroundColor: "#2A289C",
                              height: "40px",
                              minWidth: isMobile ? "80px" : "120px",
                              fontSize: isMobile ? "12px" : "14px",
                              boxShadow: "0px 2px 4px rgba(0,0,0,0.2)",
                              "&:hover": {
                                backgroundColor: "#1a1769"
                              }
                            }}
                          >
                            Verify
                          </Button>
                        )}
                        {otpVerified && (
                          <Button
                            variant="contained"
                            disabled
                            sx={{
                              backgroundColor: "green",
                              height: "40px",
                              minWidth: isMobile ? "80px" : "120px",
                              fontSize: isMobile ? "12px" : "14px",
                              boxShadow: "0px 2px 4px rgba(0,0,0,0.2)"
                            }}
                          >
                            Verified
                          </Button>
                        )}
                      </Box>
                    </Grid>
                  )}
                  <Grid container spacing={0}>
                    {/* ID Proof Section - MODIFIED FOR BETTER ROW ALIGNMENT */}
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ backgroundColor: "#f8f9e9", p: 2 }}>
                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                              <FormLabel sx={{ mr: 4, minWidth: "70px" }}>ID Proof</FormLabel>
                              <FormControl component="fieldset" sx={{ m: 0, width: "100%" }}>
                                <RadioGroup
                                  row
                                  name="identityProofType"
                                  value={formData.identityProofType}
                                  onChange={handleRadioChange}
                                  sx={{
                                    justifyContent: "flex-start",
                                    "& .MuiFormControlLabel-root": {
                                      mr: 3
                                    }
                                  }}
                                >
                                  <FormControlLabel
                                    value="aadhaar"
                                    control={<Radio size="small" sx={{ color: "gray", p: 0.5 }} />}
                                    label="Aadhaar"
                                    sx={{ mr: 2 }}
                                  />
                                  <FormControlLabel
                                    value="license"
                                    control={<Radio size="small" sx={{ color: "grey", p: 0.5 }} />}
                                    label="License"
                                  />
                                </RadioGroup>
                              </FormControl>
                            </Box>
                          </Grid>

                          {formData.identityProofType === "aadhaar" && (
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Aadhaar Number"
                                name="identityProofNumber"
                                value={formData.identityProofNumber}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, "").slice(0, 12);
                                  setFormData((prev) => ({ ...prev, identityProofNumber: value }));
                                }}
                                error={!!errors.identityProofNumber}
                                helperText={errors.identityProofNumber || " "}
                                placeholder="Enter Aadhaar Number"
                              />
                            </Grid>
                          )}

                          {formData.identityProofType === "license" && (
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                size="small"
                                label="License Number"
                                name="identityProofNumber"
                                value={formData.identityProofNumber}
                                onChange={handleChange}
                                error={!!errors.identityProofNumber}
                                helperText={errors.identityProofNumber || " "}
                                inputProps={{ maxLength: 15 }}
                                placeholder="Enter License Number"
                                onInput={(e) =>
                                  (e.target.value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 15))
                                }
                              />
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Grid>

                    {/* PAN Card Section - ALSO MODIFIED FOR CONSISTENCY */}
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ backgroundColor: "#f8f9e9", p: 2 }}>
                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                              <FormLabel sx={{ mr: 5, minWidth: "80px" }}>PAN Card</FormLabel>
                              <FormControl component="fieldset" sx={{ m: 0, width: "100%" }}>
                                <RadioGroup
                                  row
                                  name="panCard"
                                  value={formData.panCard}
                                  onChange={handlePanChange}
                                  sx={{
                                    justifyContent: "flex-start",
                                    "& .MuiFormControlLabel-root": {
                                      mr: 3
                                    }
                                  }}
                                >
                                  <FormControlLabel
                                    value="Yes"
                                    control={<Radio size="small" sx={{ color: "gray", p: 0.5 }} />}
                                    label="Yes"
                                    sx={{ mr: 2 }}
                                  />
                                  <FormControlLabel
                                    value="No"
                                    control={<Radio size="small" sx={{ color: "grey", p: 0.5 }} />}
                                    label="No"
                                  />
                                </RadioGroup>
                              </FormControl>
                            </Box>
                          </Grid>

                          {formData.panCard === "Yes" && (
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                size="small"
                                label="PAN Number"
                                placeholder="Enter PAN Number"
                                name="panCardNumber"
                                value={formData.panCardNumber}
                                onChange={handleChange}
                                error={!!errors.panCardNumber}
                                helperText={errors.panCardNumber || " "}
                                inputProps={{ maxLength: 10 }}
                                onInput={(e) =>
                                  (e.target.value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10))
                                }
                              />
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl
                      error={!!errors.privacyPolicyAccepted}
                      component="fieldset"
                      variant="standard"
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="privacyPolicyAccepted"
                            checked={formData.privacyPolicyAccepted || false}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                privacyPolicyAccepted: e.target.checked,
                              })
                            }
                          />
                        }
                        label={
                          <Typography variant="body2">
                            By providing your personal and contact details, you agree to our{" "}
                            <a
                              href="/privacy"
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#2A289C", textDecoration: "underline" }}
                            >
                              Privacy Policy
                            </a>
                            .
                          </Typography>
                        }
                      />
                      {errors.privacyPolicyAccepted && (
                        <FormHelperText>{errors.privacyPolicyAccepted}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                </Grid>
              </Box>
              <Grid container spacing={2} justifyContent="center" sx={{ mt: 2 }}>
                <Grid item>
                  {isLoading ? (
                    <CircularProgress color="success" />
                  ) : (
                    <Button
                      type="submit"
                      onSubmit={handleSubmit}
                      variant="contained"
                      disabled={isLoading || (showOtpField && !otpVerified)}
                      fullWidth
                      style={{ backgroundColor: "green", color: "white" }}
                    >
                      Register
                    </Button>
                  )}
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    fullWidth
                    style={{ backgroundColor: "#6C757D", color: "white" }}
                    onClick={handleClear}
                  >
                    Clear
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    fullWidth
                    style={{ backgroundColor: "#E52029", color: "white" }}
                    onClick={() => {
                      navigate("/");
                      window.scrollTo(0, 0);
                    }}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </Container>

      {/* Success notification */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%", fontSize: "16px", fontWeight: "bold" }}
        >
          Member Created Successfully!
        </Alert>
      </Snackbar>

      {/* Error/Info notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%", fontSize: "16px", fontWeight: "bold" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>

  );
}
export default Register;